import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, concatMap, map, mergeMap, switchMap } from 'rxjs/operators'
import {
  AddTaskAccess,
  AddTaskAccessComplete,
  ArchiveTaskAccesses,
  ArchiveTaskAccessesComplete,
  DeleteTaskAccess,
  DeleteTaskAccessComplete,
  DeleteTaskAccesses,
  DeleteTaskAccessesComplete,
  LoadTaskAccesses,
  LoadTaskAccessesComplete,
  UnarchiveTaskAccesses,
  UnarchiveTaskAccessesComplete,
  UpdateTaskAccess,
  UpdateTaskAccessComplete,
} from './actions'
import { TaskAccessService } from './service'
import { ActionFailed } from '@tokeet-frontend/tv3-platform'
import { Store } from '@ngrx/store'

@Injectable()
export class TaskAccessEffects {
  @Effect()
  loadTaskAccesses$ = this.actions$.pipe(
    ofType(LoadTaskAccesses),
    switchMap(() =>
      this.service.all().pipe(
        map((tasks) => LoadTaskAccessesComplete({ tasks })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  addTaskAccess$ = this.actions$.pipe(
    ofType(AddTaskAccess),
    concatMap(({ form }) => {
      return this.service.add(form).pipe(
        map((task) => AddTaskAccessComplete({ task })),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  updateTaskAccess$ = this.actions$.pipe(
    ofType(UpdateTaskAccess),
    switchMap(({ id, form }) =>
      this.service.update(id, form).pipe(
        map((task) => UpdateTaskAccessComplete({ update: { id: task.pkey, changes: task } })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  archiveTaskAccesses$ = this.actions$.pipe(
    ofType(ArchiveTaskAccesses),
    switchMap(({ ids }) =>
      this.service.archiveTasks(ids).pipe(
        map((task) => ArchiveTaskAccessesComplete({ updates: ids.map((id) => ({ id, changes: { archived: 1 } })) })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  unarchiveTaskAccesses$ = this.actions$.pipe(
    ofType(UnarchiveTaskAccesses),
    switchMap(({ ids }) =>
      this.service.unarchiveTasks(ids).pipe(
        map((task) => UnarchiveTaskAccessesComplete({ updates: ids.map((id) => ({ id, changes: { archived: 0 } })) })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteTaskAccess$ = this.actions$.pipe(
    ofType(DeleteTaskAccess),
    mergeMap(({ id }) =>
      this.service.delete(id).pipe(
        map(() => DeleteTaskAccessComplete({ id })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteTaskAccesses$ = this.actions$.pipe(
    ofType(DeleteTaskAccesses),
    mergeMap(({ ids }) =>
      this.service.deleteMultiple(ids).pipe(
        map((task) => DeleteTaskAccessesComplete({ ids })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  constructor(private actions$: Actions, private store: Store<any>, private service: TaskAccessService) {}
}
